import qs from 'qs'

function updateOptions(options) {
  const update = { ...options }

  const headers = {}
  Object.entries(options.headers || {}).forEach(([key, val]) => {
    if (val !== undefined && val !== null) {
      headers[key] = val
    }
  })
  update.headers = {
    'Content-Type': 'application/json',
    ...headers,
  }

  if (localStorage.jwt) {
    update.headers.Authorization = `Bearer ${localStorage.jwt}`
  }

  update.cache = 'no-store'

  return update
}

/**
 * What a failed request says, when what it says is ours to show.
 *
 * This service answers an error with a short code — `Location-not-found` — which the screens look
 * up and show in the reader's language. Anything between here and it does not: a gateway that
 * cannot reach the service answers with an HTML page, and that page used to be shown verbatim,
 * which is how a 502 arrived in the location form as several hundred lines of markup in red.
 *
 * So a body is only passed on when it could be one of ours: short, and not markup. Everything else
 * becomes a code of its own, which the screens translate like any other.
 */
const NOT_OURS = /^\s*<|<\/?(?:html|head|body|div|span|title)\b/i
const MAX_CODE_LENGTH = 200

export const failureMessage = (status, body) => {
  const text = String(body ?? '').trim()
  if (text && text.length <= MAX_CODE_LENGTH && !NOT_OURS.test(text))
    return text
  // 502/503/504 mean the request may never have reached the service — or may have been carried
  // out and only the answer lost. The two are worth telling apart from a plain failure.
  if ([502, 503, 504].includes(status)) return 'Service-unreachable'
  return 'Request-failed'
}

/**
 * What is worth knowing about a request that failed, to pass on to someone who can look into it.
 *
 * `cf-ray` is the one that matters most when the answer came from a gateway rather than from the
 * service: it names that exact request in Cloudflare's own log, so a 502 can be looked up instead
 * of guessed at. The body is kept whole here even when it is a page we refuse to show, because
 * the real reason is usually in it.
 *
 * No headers we sent: the token is one of them.
 */
const cookieBytes = () => {
  if (typeof document === 'undefined') return undefined
  const { length } = document.cookie || ''
  return length ? `${length} bytes or more` : undefined
}

const failureDetails = ({ method, url, status, body, headers, startedAt }) => ({
  at: new Date().toISOString(),
  took: `${Date.now() - startedAt} ms`,
  method,
  url,
  status,
  ray: headers?.get('cf-ray') || undefined,
  requestId: headers?.get('x-request-id') || undefined,
  // why a service behind this one refused, when it said; the message is only ever our own code
  reason: headers?.get('x-iadmin-reason') || undefined,
  body: String(body ?? '').slice(0, 2000) || undefined,
  // How much cookie rides along, because a header block too big for the proxy in front of this
  // service is refused before the service ever sees the request — and that is per person, which
  // is what makes one account fail where another does not. A floor, not a measurement: cookies
  // marked HttpOnly are sent but cannot be read from here.
  cookies: cookieBytes(),
  page: typeof window !== 'undefined' ? window.location?.href : undefined,
  agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
})

/** The details as something a person can select, copy and send on. */
export const errorReport = (details) =>
  Object.entries(details || {})
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([name, value]) => `${name}: ${value}`)
    .join('\n')

export const fetcher = async (path, options) => {
  const url = `${window.backendUrl}${path}`
  const startedAt = Date.now()
  const result = await fetch(url, updateOptions(options))

  if (result.status === 401 && window.location.pathname !== '/login') {
    window.location = '/login'
  }

  if (!result.ok) {
    const body = await result.text()
    return {
      error: failureMessage(result.status, body),
      status: result.status,
      details: failureDetails({
        method: options?.method || 'GET',
        url,
        status: result.status,
        body,
        headers: result.headers,
        startedAt,
      }),
    }
  }
  if (result.headers.get('content-type')?.includes('application/json')) {
    const response = await result.json()
    return { response, status: result.status }
  }
  return { response: await result.text(), status: result.status }
}

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    // what the screen can offer to hand on, when the message alone is not enough to act on
    this.details = details
  }
}

export default async function request(
  method,
  urlPath,
  body = {},
  options = {}
) {
  const opt = { ...options, method }
  // The path may already carry a query. It is taken off here and put back once: appending the
  // built query to a path that still had its own sent it twice, joined by a second `?` — and the
  // far side then read `country=SE&...&perPage=20?country=SE` as country being a list of two,
  // which matched no location at all.
  const [path, inlineQuery = ''] = urlPath.split('?')
  const isGET = method.toUpperCase() === 'GET'
  let query = inlineQuery && `?${inlineQuery}`
  if (!isGET) {
    opt.body = JSON.stringify(body)
  } else {
    const merged = qs.stringify({ ...qs.parse(inlineQuery), ...body })
    query = merged && `?${merged}`
  }
  const { error, response, status, details } = await fetcher(
    `${encodeURI(path)}${query || ''}`,
    opt
  )
  if (error) {
    throw new ApiError(error, status, details)
  }
  return response
}
